/**
 * Criterion Living - Products Collection Page
 * Premium Educational Landing Page Scripts
 * ==========================================
 * 
 * Shopify Compatible JavaScript Module
 * Handles scroll-based reveal animations and smooth interactions
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    revealThreshold: 0.15,
    revealRootMargin: '0px 0px -50px 0px',
    scrollOffset: 80
  };

  /**
   * Initialize scroll reveal animations using Intersection Observer
   */
  function initScrollReveal() {
    const revealElements = document.querySelectorAll('.cl-reveal');
    
    if (!revealElements.length) return;

    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all elements immediately
      revealElements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: CONFIG.revealRootMargin,
      threshold: CONFIG.revealThreshold
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    revealElements.forEach(el => observer.observe(el));
  }

  /**
   * Smooth scroll to section when clicking scroll indicator
   */
  function initSmoothScroll() {
    const scrollIndicator = document.querySelector('.cl-hero__scroll');
    const introSection = document.querySelector('.cl-intro');

    if (scrollIndicator && introSection) {
      scrollIndicator.addEventListener('click', (e) => {
        e.preventDefault();
        const targetPosition = introSection.getBoundingClientRect().top + window.pageYOffset - CONFIG.scrollOffset;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      });
    }
  }

  /**
   * Add subtle parallax effect to category hero images
   */
  function initParallax() {
    const heroImages = document.querySelectorAll('.cl-category__hero-image');
    
    if (!heroImages.length) return;

    // Only enable on larger screens for performance
    if (window.innerWidth < 768) return;

    let ticking = false;

    function updateParallax() {
      heroImages.forEach(img => {
        const container = img.closest('.cl-category__hero');
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if element is in viewport
        if (rect.bottom < 0 || rect.top > windowHeight) return;

        // Calculate parallax offset
        const scrollPercent = (rect.top + rect.height) / (windowHeight + rect.height);
        const parallaxOffset = (scrollPercent - 0.5) * 30;
        
        img.style.transform = `translateY(${parallaxOffset}px) scale(1.05)`;
      });
      
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }, { passive: true });

    // Initial call
    updateParallax();
  }

  /**
   * Handle category section navigation highlighting
   */
  function initSectionTracking() {
    const sections = document.querySelectorAll('.cl-category');
    
    if (!sections.length) return;

    const observerOptions = {
      root: null,
      rootMargin: '-30% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Update URL hash without scrolling (optional feature)
          const sectionId = entry.target.getAttribute('id');
          if (sectionId && history.replaceState) {
            history.replaceState(null, null, `#${sectionId}`);
          }
        }
      });
    }, observerOptions);

    sections.forEach(section => observer.observe(section));
  }

  /**
   * Initialize accordion interactions
   */
  function initAccordion() {
    const accordionItems = document.querySelectorAll('.cl-accordion__item');
    
    accordionItems.forEach(item => {
      const trigger = item.querySelector('.cl-accordion__trigger');
      
      if (trigger) {
        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          
          // Close all items in the same accordion
          const accordion = item.closest('.cl-accordion');
          if (accordion) {
            accordion.querySelectorAll('.cl-accordion__item').forEach(otherItem => {
              otherItem.classList.remove('is-open');
            });
          }
          
          // Toggle current item
          if (!isOpen) {
            item.classList.add('is-open');
          }
        });
      }
    });
    
    // Guidance section collapse toggle
    const guidanceHeaders = document.querySelectorAll('.cl-category__guidance-header');
    
    guidanceHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const guidance = header.closest('.cl-category__guidance');
        if (guidance) {
          guidance.classList.toggle('is-collapsed');
        }
      });
    });
  }

  /**
   * Initialize category navigation
   */
  function initCategoryNav() {
    const navLinks = document.querySelectorAll('.cl-nav__link');
    const sections = document.querySelectorAll('.cl-category[id]');
    
    // Smooth scroll on click
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetSection = document.getElementById(targetId);
        
        if (targetSection) {
          const navHeight = document.querySelector('.cl-nav')?.offsetHeight || 0;
          const targetPosition = targetSection.getBoundingClientRect().top + window.pageYOffset - navHeight;
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
    
    // Update active state on scroll
    if (sections.length && navLinks.length) {
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -80% 0px',
        threshold: 0
      };
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('id');
            navLinks.forEach(link => {
              link.classList.remove('is-active');
              if (link.getAttribute('href') === `#${id}`) {
                link.classList.add('is-active');
              }
            });
          }
        });
      }, observerOptions);
      
      sections.forEach(section => observer.observe(section));
    }
  }

  /**
   * Initialize subcategory tabs
   */
  function initSubcategoryTabs() {
    const tabContainers = document.querySelectorAll('.cl-subcategory-tabs');
    
    tabContainers.forEach(container => {
      const tabs = container.querySelectorAll('.cl-subcategory-tab');
      const parent = container.closest('.cl-category__products');
      
      if (!parent) return;
      
      const contents = parent.querySelectorAll('.cl-subcategory-content');
      
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const targetId = tab.getAttribute('data-tab');
          
          // Update active tab
          tabs.forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          
          // Update active content
          contents.forEach(content => {
            content.classList.remove('is-active');
            if (content.getAttribute('data-content') === targetId) {
              content.classList.add('is-active');
            }
          });
        });
      });
    });
  }

  /**
   * Add hover effects to application tags
   */
  function initTagInteractions() {
    const tags = document.querySelectorAll('.cl-category__application-tag');
    
    tags.forEach(tag => {
      tag.addEventListener('mouseenter', () => {
        tag.style.transform = 'translateY(-2px)';
      });
      
      tag.addEventListener('mouseleave', () => {
        tag.style.transform = 'translateY(0)';
      });
    });
  }

  /**
   * Preload critical images for smoother experience
   */
  function preloadImages() {
    const heroImages = document.querySelectorAll('.cl-category__hero-image, .cl-hero__image');
    
    heroImages.forEach(img => {
      if (img.dataset.src) {
        const preloadImg = new Image();
        preloadImg.src = img.dataset.src;
        preloadImg.onload = () => {
          img.src = img.dataset.src;
          img.classList.add('is-loaded');
        };
      }
    });
  }

  /**
   * Handle reduced motion preference
   */
  function checkReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      document.documentElement.classList.add('reduced-motion');
      // Make all reveal elements visible immediately
      document.querySelectorAll('.cl-reveal').forEach(el => {
        el.classList.add('is-visible');
      });
    }
  }

  /**
   * Initialize all functionality when DOM is ready
   */
  function init() {
    // Check for reduced motion first
    checkReducedMotion();
    
    // Initialize all modules
    initScrollReveal();
    initSmoothScroll();
    initParallax();
    initSectionTracking();
    initAccordion();
    initCategoryNav();
    initSubcategoryTabs();
    initTagInteractions();
    preloadImages();

    // Log initialization for debugging
    console.log('Criterion Living Products Page initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose init function for Shopify section reloads
  window.CriterionLivingProducts = {
    init: init,
    reinit: function() {
      init();
    }
  };

})();